import { motion, AnimatePresence } from 'framer-motion';
import { Check } from 'lucide-react';

/**
 * QuizMilestoneToast — Dopamine "bloc validé" toast that fires after each
 * completed screen. Inspired by Duolingo / Notion-style streak feedback.
 *
 * Props:
 *   - show: boolean — controls visibility
 *   - label: string — e.g. "Bloc finance validé"
 *   - icon: optional emoji
 *   - subline: optional secondary text (e.g. "Diagnostic financier prêt")
 */
const QuizMilestoneToast = ({ show, label, icon = '✓', subline }) => (
  <AnimatePresence>
    {show && (
      <motion.div
        initial={{ opacity: 0, y: -40, scale: 0.85 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -20, scale: 0.9 }}
        transition={{ type: 'spring', stiffness: 320, damping: 22 }}
        className="fixed top-6 left-1/2 -translate-x-1/2 z-[60] pointer-events-none px-4"
        data-testid="quiz-milestone-toast"
      >
        <div
          className="flex items-center gap-3 rounded-2xl px-4 py-3 shadow-2xl border max-w-sm"
          style={{
            background: 'linear-gradient(135deg, #1A1A1A 0%, #2A1810 100%)',
            borderColor: 'rgba(240,90,26,0.45)',
            boxShadow: '0 18px 40px rgba(0,0,0,0.45), 0 0 26px rgba(240,90,26,0.32)',
          }}
        >
          {/* Animated check icon */}
          <motion.div
            initial={{ scale: 0, rotate: -90 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.15, type: 'spring', stiffness: 360, damping: 14 }}
            className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
            style={{
              background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
              boxShadow: '0 0 18px rgba(34,197,94,0.55)',
            }}
          >
            {typeof icon === 'string' && icon.length === 1 ? (
              <span className="text-white text-lg">{icon}</span>
            ) : (
              <Check className="w-5 h-5 text-white" strokeWidth={3.5} />
            )}
          </motion.div>

          <div className="flex-1 min-w-0">
            <p className="text-white font-black text-sm leading-tight">
              {label}
            </p>
            {subline && (
              <p className="text-white/60 text-[11px] leading-tight mt-0.5">
                {subline}
              </p>
            )}
          </div>

          {/* Bottom progress bar that "fills" then auto-dismisses */}
          <motion.div
            className="absolute bottom-0 left-0 right-0 h-[2px] rounded-b-2xl"
            initial={{ scaleX: 1, transformOrigin: 'left' }}
            animate={{ scaleX: 0 }}
            transition={{ duration: 1.8, ease: 'linear' }}
            style={{
              background: 'linear-gradient(90deg, #F05A1A, #FBBF24)',
            }}
          />
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

export default QuizMilestoneToast;
